/**
 * Shared single-flight protection for order-creation thunks.
 *
 * On slow mobile devices a fast double tap can fire onClick twice before the
 * button's disabled state renders, which would POST two orders. The Trustpilot
 * slice keeps a tab-scoped guard around its create thunk.
 */

/**
 * rejectWithValue sentinel for a suppressed duplicate order submission.
 * Callers must not treat it as a real failure (no error page, no
 * checkout_error event): the first submission owns this checkout.
 */
export const ORDER_SUBMISSION_IN_PROGRESS = "order_submission_in_progress";

export type SingleFlightOrderGuard = {
  /** Returns false when another submission of this thunk is still in flight. */
  tryAcquire: (requestId: string) => boolean;
  /** Releases the lock; ignores stale request ids. */
  release: (requestId: string) => void;
};

export const createSingleFlightOrderGuard = (): SingleFlightOrderGuard => {
  let inflightRequestId: string | null = null;

  return {
    tryAcquire: (requestId: string) => {
      if (inflightRequestId !== null) {
        return false;
      }
      inflightRequestId = requestId;
      return true;
    },
    release: (requestId: string) => {
      if (inflightRequestId === requestId) {
        inflightRequestId = null;
      }
    },
  };
};

/**
 * Rejected-reducer helper: the duplicate sentinel is control flow, not a
 * checkout error, so it must never reach `state.error` and render to customers.
 */
export const isDuplicateOrderSubmission = (payload: unknown): boolean =>
  payload === ORDER_SUBMISSION_IN_PROGRESS;
